import * as React from 'react';
import {Mutation} from 'react-apollo';

import type {
    DeleteEmailSequenceTemplate as DeleteEmailSequenceTemplateMutation,
    DeleteEmailSequenceTemplateVariables as DeleteEmailSequenceTemplateMutationVariables,
    UndeleteEmailSequenceTemplate as UndeleteEmailSequenceTemplateMutation,
    UndeleteEmailSequenceTemplateVariables as UndeleteEmailSequenceTemplateMutationVariables,
    GetUserEmailSequenceTemplates as GetUserEmailSequenceTemplatesQuery,
    GetSharedEmailSequenceTemplates as GetSharedEmailSequenceTemplatesQuery,
    GetArchivedEmailSequenceTemplates as GetArchivedEmailSequenceTemplatesQuery,
} from 'nutshell-graphql-types';

import DELETE_EMAIL_SEQUENCE_TEMPLATE from '../../graphql/mutations/delete-email-sequence-template.graphql';
import UPDATE_EMAIL_SEQUENCE_TEMPLATE from '../../graphql/mutations/undelete-email-sequence-template.graphql';
import GET_ARCHIVED_EMAIL_SEQUENCE_TEMPLATES from '../../graphql/queries/get-archived-email-sequence-templates.graphql';
import GET_ARCHIVED_SEQUENCE_IDS from '../../graphql/queries/get-archived-sequence-ids.graphql';
import GET_SHARED_EMAIL_SEQUENCE_TEMPLATES from '../../graphql/queries/get-shared-email-sequence-templates.graphql';
import GET_USER_EMAIL_SEQUENCE_TEMPLATES from '../../graphql/queries/get-user-email-sequence-templates.graphql';

import type {TemplateItem} from './template-list';
import {TemplateListRowComponent} from './template-list-row-component';

class DeleteSequenceMutation extends Mutation<
    DeleteEmailSequenceTemplateMutation,
    DeleteEmailSequenceTemplateMutationVariables
> {}
class UndeleteSequenceMutation extends Mutation<
    UndeleteEmailSequenceTemplateMutation,
    UndeleteEmailSequenceTemplateMutationVariables
> {}

type Props = {
    showCreator: boolean;
    template: TemplateItem;
    onSequenceRename: (templateId: string, currentName: string) => void;
};

export const TemplateListRow = (props: Props) => (
    <DeleteSequenceMutation
        mutation={DELETE_EMAIL_SEQUENCE_TEMPLATE}
        refetchQueries={[{query: GET_ARCHIVED_SEQUENCE_IDS}]}
        update={(proxy, {data}) => {
            if (!data) return;
            const deletedSequenceTemplate = data.emailSequenceTemplateDelete.emailSequenceTemplate;

            // User Templates
            let userCacheResult: GetUserEmailSequenceTemplatesQuery | null | undefined;
            try {
                userCacheResult = proxy.readQuery({
                    query: GET_USER_EMAIL_SEQUENCE_TEMPLATES,
                });
            } catch (_e: any) {
                // https://github.com/apollographql/apollo-feature-requests/issues/1
                // Fixed in V3
            }
            if (userCacheResult) {
                const cachedCollection = userCacheResult.emailSequenceTemplateCollection;
                const cachedTemplates = cachedCollection.userTemplates;
                proxy.writeQuery({
                    query: GET_USER_EMAIL_SEQUENCE_TEMPLATES,
                    data: {
                        emailSequenceTemplateCollection: {
                            ...cachedCollection,
                            userTemplates: cachedTemplates.filter(
                                (sequence) => sequence.id !== deletedSequenceTemplate.id
                            ),
                        },
                    },
                });
            }

            // Shared templates
            let sharedCacheResult: GetSharedEmailSequenceTemplatesQuery | null | undefined;
            try {
                sharedCacheResult = proxy.readQuery({
                    query: GET_SHARED_EMAIL_SEQUENCE_TEMPLATES,
                });
            } catch (_e: any) {
                // https://github.com/apollographql/apollo-feature-requests/issues/1
                // Fixed in V3
            }
            if (sharedCacheResult) {
                const cachedCollection = sharedCacheResult.emailSequenceTemplateCollection;
                const cachedTemplates = cachedCollection.sharedTemplates;
                proxy.writeQuery({
                    query: GET_SHARED_EMAIL_SEQUENCE_TEMPLATES,
                    data: {
                        emailSequenceTemplateCollection: {
                            ...sharedCacheResult.emailSequenceTemplateCollection,
                            // If an admin archived a shared template, remove it from that list
                            sharedTemplates: cachedTemplates.filter(
                                (sequence) => sequence.id !== deletedSequenceTemplate.id
                            ),
                        },
                    },
                });
            }

            // Archived templates
            let archivedCacheResult: GetArchivedEmailSequenceTemplatesQuery | null | undefined;
            try {
                archivedCacheResult = proxy.readQuery({
                    query: GET_ARCHIVED_EMAIL_SEQUENCE_TEMPLATES,
                });
            } catch (_e: any) {
                // https://github.com/apollographql/apollo-feature-requests/issues/1
                // Fixed in V3
            }
            if (archivedCacheResult) {
                const cachedCollection = archivedCacheResult.emailSequenceTemplateCollection;
                const cachedTemplates = cachedCollection.archivedTemplates;
                proxy.writeQuery({
                    query: GET_ARCHIVED_EMAIL_SEQUENCE_TEMPLATES,
                    data: {
                        emailSequenceTemplateCollection: {
                            ...archivedCacheResult.emailSequenceTemplateCollection,
                            archivedTemplates: cachedTemplates.concat(deletedSequenceTemplate),
                        },
                    },
                });
            }
        }}
    >
        {(deleteSequenceTemplate) => (
            <UndeleteSequenceMutation
                mutation={UPDATE_EMAIL_SEQUENCE_TEMPLATE}
                refetchQueries={[{query: GET_ARCHIVED_SEQUENCE_IDS}]}
                update={(proxy, {data}) => {
                    if (!data) return;
                    const undeletedSequenceTemplate =
                        data.emailSequenceTemplateUndelete.emailSequenceTemplate;

                    // User Templates
                    let userCacheResult: GetUserEmailSequenceTemplatesQuery | null | undefined;
                    try {
                        userCacheResult = proxy.readQuery({
                            query: GET_USER_EMAIL_SEQUENCE_TEMPLATES,
                        });
                    } catch (_e: any) {
                        // https://github.com/apollographql/apollo-feature-requests/issues/1
                        // Fixed in V3
                    }
                    if (userCacheResult) {
                        const cachedCollection = userCacheResult.emailSequenceTemplateCollection;
                        const cachedTemplates = cachedCollection.userTemplates;
                        proxy.writeQuery({
                            query: GET_USER_EMAIL_SEQUENCE_TEMPLATES,
                            data: {
                                emailSequenceTemplateCollection: {
                                    ...cachedCollection,
                                    // If the template being undeleted is shared by someone else, don't add it to user-templates
                                    userTemplates: undeletedSequenceTemplate.isImmutable
                                        ? cachedTemplates
                                        : cachedTemplates.concat(undeletedSequenceTemplate),
                                },
                            },
                        });
                    }

                    // Shared templates
                    let sharedCacheResult: GetSharedEmailSequenceTemplatesQuery | null | undefined;
                    try {
                        sharedCacheResult = proxy.readQuery({
                            query: GET_SHARED_EMAIL_SEQUENCE_TEMPLATES,
                        });
                    } catch (_e: any) {
                        // https://github.com/apollographql/apollo-feature-requests/issues/1
                        // Fixed in V3
                    }
                    if (sharedCacheResult) {
                        const cachedCollection = sharedCacheResult.emailSequenceTemplateCollection;
                        const cachedTemplates = cachedCollection.sharedTemplates;
                        proxy.writeQuery({
                            query: GET_SHARED_EMAIL_SEQUENCE_TEMPLATES,
                            data: {
                                emailSequenceTemplateCollection: {
                                    ...cachedCollection,
                                    // If it's shared, we _do_ need to add it to the shared section
                                    sharedTemplates: undeletedSequenceTemplate.isImmutable
                                        ? cachedTemplates.concat(undeletedSequenceTemplate)
                                        : cachedTemplates,
                                },
                            },
                        });
                    }

                    // Archived templates
                    let archivedCacheResult:
                        | GetArchivedEmailSequenceTemplatesQuery
                        | null
                        | undefined;
                    try {
                        archivedCacheResult = proxy.readQuery({
                            query: GET_ARCHIVED_EMAIL_SEQUENCE_TEMPLATES,
                        });
                    } catch (_e: any) {
                        // https://github.com/apollographql/apollo-feature-requests/issues/1
                        // Fixed in V3
                    }
                    if (archivedCacheResult) {
                        const cachedCollection =
                            archivedCacheResult.emailSequenceTemplateCollection;
                        const cachedTemplates = cachedCollection.archivedTemplates;
                        proxy.writeQuery({
                            query: GET_ARCHIVED_EMAIL_SEQUENCE_TEMPLATES,
                            data: {
                                emailSequenceTemplateCollection: {
                                    ...cachedCollection,
                                    archivedTemplates: cachedTemplates.filter(
                                        (sequence) => sequence.id !== undeletedSequenceTemplate.id
                                    ),
                                },
                            },
                        });
                    }
                }}
            >
                {(undeleteSequenceTemplate) => (
                    <TemplateListRowComponent
                        {...props}
                        deleteSequenceTemplate={(sequenceTemplateId) =>
                            deleteSequenceTemplate({
                                variables: {
                                    sequenceTemplate: {
                                        id: sequenceTemplateId,
                                    },
                                },
                            })
                        }
                        undeleteSequenceTemplate={(sequenceTemplateId) =>
                            undeleteSequenceTemplate({
                                variables: {
                                    sequenceTemplate: {
                                        id: sequenceTemplateId,
                                    },
                                },
                            })
                        }
                    />
                )}
            </UndeleteSequenceMutation>
        )}
    </DeleteSequenceMutation>
);
